import { useTranslation } from 'react-i18next';
import React from 'react'
import { DateTimePicker, LocalizationProvider} from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import esLocale from 'date-fns/locale/es';
import { FormHelperText } from '@mui/material';
import {
    CustomStyleDatePicker,
    CustomStyleDatePickerRequired,
    CustomStyleFormHelperText
} from './Styles';
import Stack from "@mui/material/Stack";
import {CustomTextField} from "./CustomTextField";
import {getDateFormat} from "../Apis/functions";
import InputAdornment from "@mui/material/InputAdornment";
import {CalendarMonthOutlined} from "@mui/icons-material";
import CustomDateTimePickerApp from "./CustomDateTimePicker/CustomDateTimePickerApp";
import {DATE_TIME_PICKER_MODEL} from "./CustomDateRangePicker/Model";
import Cookies from "js-cookie";
import dateInputLanguage from "../hooks/dateInputLanguage";

/**
 * @Author Cecilia López Martínez
 * @JobTitle Full Stack Developer - IBL
 * @Email clopez@ibl.mx
 *
 * @description
 * DateTimePicker component for selecting date and time, with configurable validation, formatting, and UI behavior.
 *
 * @param {string} id - Identifier for the DateTimePicker component (required).
 * @param {string} title - Title or label shown above the input (required).
 * @param {string} name - Name of the form field or model receiving the input value (required).
 * @param {Date} value - The current value of the component (required).
 * @param {boolean} disabled - If true, the input is disabled and cannot be interacted with (required).
 * @param {boolean} emptyFields - Indicates whether the input is flagged as empty/invalid (required).
 * @param {boolean} required - If true, the input is marked as required (required).
 * @param {boolean} disablePast - If true, disables selection of past dates (optional).
 * @param {boolean} disableFuture - If true, disables selection of future dates (optional).
 * @param {object} trans - Object containing translated labels or strings (optional).
 * @param {Array<string>} views - Defines which views are available in the picker (e.g., ['year', 'month', 'day']).
 * @param {boolean} ampm - Whether to use 12-hour (AM/PM) time format (optional).
 * @param {boolean} allowSameDateSelection - If true, allows selecting the same date again (optional).
 * @param {boolean} isValid - Flag indicating whether the current value is valid (optional).
 * @param {string} isValidText - Text message shown when the value is valid (optional).
 * @param {string} helperText - Helper or validation text shown below the input (optional).
 * @param {boolean} onlySelecting - If true, disables confirmation and assumes user is just selecting a value (optional).
 * @param {function} onError - Callback triggered when an error occurs (e.g., invalid value or format) (optional).
 * @param {Date} minDate - Minimum allowed date (optional).
 * @param {function} onChange - Alternative callback to handle value changes (optional).
 *
 * @returns {JSX.Element} A rendered DateTimePicker component.
 */

const CustomDateTimePicker = ({ id = "dp_cdtp_datetime", trans, name, value, disabled = false,
                                  emptyFields = false, required = false, disablePast = false, disableFuture = false,
                                  views=['year', 'month', 'day', 'hours', 'minutes', 'seconds'],
                                  ampm=false,allowSameDateSelection=true,
                                  isValid=true, isValidText="date_invalid",maxDate=undefined, helperText, onlySelecting=false,
                                  onError=() => {},minDate=undefined,onChange,maxDateTime=undefined,minDateTime=undefined,
                              }) => {
    const { t } = useTranslation();
    const lCode = Cookies.get('i18next') || 'en';
    const {
        elementRef,
        calculateTop,
        handleKeyDown,
        getPopperStyles
    } = CustomDateTimePickerApp({ onlySelecting, type: DATE_TIME_PICKER_MODEL.DATE_TIME });


    return<>
        {
            !disabled ?
                <div ref={elementRef}>
                    <LocalizationProvider disableScrollLock={true} dateAdapter={AdapterDateFns} locale={dateInputLanguage(lCode)}
                                          localeText={{ fieldYearPlaceholder: (params) => 'A'.repeat(params.digitAmount) }}>
                        <Stack spacing={3} >
                            <DateTimePicker
                                sx={((emptyFields && (value === '' || value === null)) || (!isValid && value !== '' && value !== null))
                                    ? CustomStyleDatePickerRequired
                                    : CustomStyleDatePicker(helperText && !emptyFields && isValid)}
                                allowSameDateSelection={allowSameDateSelection}
                                id={id}
                                label={`${t(trans)}`}
                                name={name}
                                className={((emptyFields && (value === '' || value === null)) || (!isValid && value !== '' && value !== null)) ? "input-required" : ""}
                                value={value}
                                onChange={(e)=>{onChange(e, name)}}
                                required={required}
                                disabled={disabled}
                                disablePast={disablePast}
                                disableFuture={disableFuture}
                                views={views}
                                ampm={ampm}
                                minDate={ minDate}
                                maxDate={ maxDate}
                                maxDateTime={ maxDateTime}
                                minDateTime={ minDateTime}
                                defaultValue={null}
                                onError={(error) => onError(error)}
                                onOpen={() => calculateTop(DATE_TIME_PICKER_MODEL.DATE_TIME)}
                                slotProps={{
                                    textField: {
                                        variant: 'outlined',
                                        required : required,
                                        id: id,
                                        onKeyDown: handleKeyDown,
                                    },
                                    popper: getPopperStyles()
                                }}
                            />
                        </Stack>
                    </LocalizationProvider>
                    {emptyFields && (value === '' || value === null) &&
                    <FormHelperText error={true} sx={CustomStyleFormHelperText}>{t('requiredField')}</FormHelperText>
                    }

                    {!isValid && value !== '' &&
                    <FormHelperText error={true} sx={CustomStyleFormHelperText}>{t(isValidText)}</FormHelperText>
                    }

                    { helperText && !emptyFields && isValid &&
                    <FormHelperText className={"css-k4qjio-MuiFormHelperText-root"} sx={CustomStyleFormHelperText}>{t(helperText)}</FormHelperText>
                    }
                </div>
                :
                <>
                    <CustomTextField id={id}
                                     value={ value ? getDateFormat(value, true) : null}
                                     disabled={disabled}
                                     trans={trans}
                                     type={"text"}
                                     required={required}
                                     InputProps={{
                                         endAdornment: (
                                             <InputAdornment position="end">
                                                 <CalendarMonthOutlined/>
                                             </InputAdornment>
                                         ),
                                     }}
                                     helperText={helperText}
                    />
                </>
        }
    </>
}
export default CustomDateTimePicker
